import PropTypes from 'prop-types';
import { memo, useEffect, useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import Popover from '@mui/material/Popover';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import _ from 'lodash';
import formatTravelMode from '../../../../helpers/formatTravelMode';
import { setItineraryOnHold } from '../../../../store/slices/trip';
import { TravelTypes, PaymentTypeCodes } from '../../../../constants';
import checkItineraryCanHold from '../../../../helpers/checkItineraryCanHold';
import checkIsPaymentRequiredForItinerary from '../../../../helpers/checkIsPaymentRequiredForItinerary';
import getCurrentPageURL from '../../../../helpers/getCurrentPageURL';

const BookingActions = ({ itinerary, tripId }) => {
  const { track } = useAnalytics();
  const canHoldItinerary = checkItineraryCanHold(itinerary);
  const [disabled, setDisabled] = useState(!canHoldItinerary);
  const [anchorEl, setAnchorEl] = useState(null);
  const [disabledFlightMessage, setDisabledFlightMessage] = useState(null);
  const dispatch = useDispatch();
  const trip = useSelector((store) => store.trip.selectedTrips[tripId]);
  const itineraryMode = itinerary && itinerary.mode;
  const itineraryId = (itinerary && itinerary.id) || 0;

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (event) => {
    dispatch(
      setItineraryOnHold({
        tripId,
        itineraryMode,
        itineraryId,
      }),
    );
    track('tripConfiguration.handleBookingActionChange', {
      tripId,
      itineraryMode,
      page: getCurrentPageURL(),
      bookingAction:
        event && event.currentTarget && event.currentTarget.value === 'true' ? 'Hold' : 'Book',
    });
  };

  const formatPopoverContent = () => {
    if (!disabled) {
      return `Select whether book or hold ${formatTravelMode(itineraryMode)}`;
    }
    if (itineraryMode === TravelTypes.hotel) {
      if (checkIsPaymentRequiredForItinerary(itinerary)) {
        return "Hotel requires a form of payment, and cannot be held without payment. Please remove from trip if you'd like to hold the rest of the trip components";
      }
      return 'The hotel does not require payment when making the reservation.';
    }
    if (itineraryMode === TravelTypes.carrental) {
      let reason = 'is prepaid, ';
      if (itinerary.priceInfo.paymentTypeCode === PaymentTypeCodes.DEPOSIT)
        reason = 'require deposit which';
      else if (itinerary.priceInfo.paymentTypeCode === PaymentTypeCodes.GUARANTEE)
        reason = 'require guarantee which';
      if (itinerary.priceInfo.paymentTypeCode !== null) {
        return `Rental car rate ${reason} requires a form of payment, and cannot be held without payment. Please remove from trip if you'd like to hold the rest of the trip components`;
      }
      return 'Rental Car Payment Due at Pickup';
    }
    if (itineraryMode === TravelTypes.flight && disabledFlightMessage) {
      return disabledFlightMessage;
    }
    if (itineraryMode === TravelTypes.tour) {
      return 'Tour cannot be held without payment.';
    }
    return '';
  };

  useEffect(() => {
    if (canHoldItinerary && itinerary && itinerary.mode === TravelTypes.flight) {
      // Book/hotel dropdown should disabled for flight if hotel or prepaid rental car in trip
      // As we are creating single PNR, we need to add payment method
      // to PNR to book hotel or prepaid rental car
      let disabledFlight = '';
      if (
        trip.rentalCarItineraries &&
        _.find(trip.rentalCarItineraries, (r) => checkIsPaymentRequiredForItinerary(r))
      ) {
        disabledFlight =
          'Could not hold flight because rental car which added to trip require credit card. As we are creating single PNR for both rental car and flight, payment method will added to PNR.  Please remove rental car from trip to hold flight booking without adding payment method.';
      } else if (
        trip.hotelItineraries &&
        Object.keys(trip.hotelItineraries).length > 0 &&
        _.find(trip.hotelItineraries, (r) => checkIsPaymentRequiredForItinerary(r))
      ) {
        disabledFlight =
          'Could not hold flight because hotel itinerary added to trip. As we are creating single PNR for both hotel and flight, payment method will added to PNR. Please remove hotel from trip to hold flight booking without adding payment method.';
      }
      if (disabledFlight) {
        setDisabled(true);
        setDisabledFlightMessage(disabledFlight);

        if (_.find(trip.itinerariesOnHold, (it) => it.itineraryMode === TravelTypes.flight)) {
          dispatch(
            setItineraryOnHold({
              tripId,
              itineraryMode,
              itineraryId,
            }),
          );
        }
      } else {
        setDisabled(false);
      }
    }
  }, [
    canHoldItinerary,
    dispatch,
    itinerary,
    itineraryId,
    itineraryMode,
    trip.hotelItineraries,
    trip.itinerariesOnHold,
    trip.rentalCarItineraries,
    tripId,
  ]);

  const open = Boolean(anchorEl);
  return !itinerary ? (
    <></>
  ) : (
    <div className="flex items-center mb-2 mt-1">
      <select
        className="border rounded border-gray-600"
        onChange={handleChange}
        value={(
          (trip &&
            trip.itinerariesOnHold &&
            !!_.find(
              trip.itinerariesOnHold,
              (i) => i.itineraryMode === itineraryMode && i.itineraryId === itineraryId,
            )) ||
          false
        ).toString()}
        disabled={disabled}
      >
        <option value="false">Book</option>
        <option value="true">Hold</option>
      </select>
      <FaInfoCircle
        className="inline ml-1 text-base cursor-pointer"
        aria-owns={open ? 'mouse-over-popover' : undefined}
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
      />
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <div className="p-4 max-w-sm">{formatPopoverContent()}</div>
      </Popover>
    </div>
  );
};

BookingActions.propTypes = {
  itinerary: PropTypes.shape().isRequired,
  tripId: PropTypes.string.isRequired,
};

export default memo(BookingActions);
